import { useEffect, useState, type ReactNode } from "react";
import { adminApi, DESTINATION_LABEL, type BroadcastSettings, type FeatureToggles } from "@/lib/admin";

export function AdminSettings() {
  const [settings, setSettings] = useState<BroadcastSettings | null>(null);
  const [voiceId, setVoiceId] = useState("");
  const [destination, setDestination] = useState(0);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<Date | null>(null);
  const [err, setErr] = useState<string | null>(null);

  async function refresh() {
    try {
      const s = await adminApi.getSettings();
      setSettings(s);
      setVoiceId(s.donorVoiceId);
      setDestination(s.phase1Destination);
      setErr(null);
    } catch (e) {
      setErr((e as Error).message);
    }
  }

  useEffect(() => {
    refresh();
  }, []);

  async function save() {
    setSaving(true);
    setErr(null);
    try {
      const updated = await adminApi.updateSettings({
        donorVoiceId: voiceId.trim(),
        phase1Destination: destination,
      });
      setSettings(updated);
      setSavedAt(new Date());
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  if (!settings) return <p className="font-mono text-sm">{err ?? "Ładuję ustawienia…"}</p>;

  const dirty = voiceId.trim() !== settings.donorVoiceId || destination !== settings.phase1Destination;

  return (
    <div className="space-y-6 max-w-3xl">
      <header>
        <h1 className="font-display font-bold text-3xl">Ustawienia broadcastu</h1>
        <p className="text-sm text-carp-ink/70 mt-1">
          Live tweaki — działają od razu po zapisie, bez restartu kontenera.
        </p>
      </header>

      {err && <div className="brut bg-carp-destructive text-white p-3 text-sm">{err}</div>}

      <section className="brut bg-carp-paper p-5 space-y-3">
        <div>
          <label htmlFor="dest" className="label-brut">
            Gdzie czytana jest wiadomość donatora (Phase 1)
          </label>
          <select
            id="dest"
            className="input-brut"
            value={destination}
            onChange={(e) => setDestination(parseInt(e.target.value, 10))}
          >
            {Object.entries(DESTINATION_LABEL).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </select>
          <p className="text-xs text-carp-ink/60 mt-1 font-mono">
            Phase 2 (odpowiedź MudCarpa) zawsze leci na Karpia.
          </p>
        </div>

        <div>
          <label htmlFor="voice" className="label-brut">
            Voice ID dla wiadomości donatora (ElevenLabs)
          </label>
          <input
            id="voice"
            type="text"
            className="input-brut font-mono text-sm"
            value={voiceId}
            onChange={(e) => setVoiceId(e.target.value)}
            placeholder="np. KsApdvL1DTvt6I1rfBnM"
            spellCheck={false}
          />
          <p className="text-xs text-carp-ink/60 mt-1">
            Głos Karpia (Phase 2) jest stały — pochodzi z ELEVENLABS_VOICE_ID env i nie podlega zmianie tutaj.
          </p>
        </div>

        <div className="flex items-center gap-3 pt-2">
          <button
            type="button"
            className="btn-primary"
            disabled={!dirty || saving}
            onClick={save}
          >
            {saving ? "Zapisuję…" : "Zapisz"}
          </button>
          {dirty && !saving && <span className="text-xs font-mono text-carp-ink/60">↑ zmiany niezapisane</span>}
          {savedAt && !dirty && (
            <span className="text-xs font-mono text-carp-lime">
              ✓ zapisano {savedAt.toLocaleTimeString("pl-PL")}
            </span>
          )}
        </div>
      </section>

      <section className="brut bg-carp-yellow p-4 text-sm">
        <p className="font-display font-bold uppercase text-xs mb-2">Dla overlay (OBS)</p>
        Gdy destination = <b>Overlay</b> lub <b>Both</b>, warstwa OBS (
        <code className="font-mono bg-carp-ink/10 px-1">/overlay</code>) odtwarza Phase 1 audio
        sama (przez {"<audio>"}). Phase 2 (Karp) zawsze leci na fizyczne urządzenie.
      </section>

      <FeatureTogglesSection />
    </div>
  );
}

function FeatureTogglesSection() {
  const [toggles, setToggles] = useState<FeatureToggles | null>(null);
  const [working, setWorking] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function refresh() {
    try {
      setToggles(await adminApi.getToggles());
      setErr(null);
    } catch (e) {
      setErr((e as Error).message);
    }
  }

  useEffect(() => {
    refresh();
  }, []);

  async function flip(patch: Partial<FeatureToggles>) {
    setWorking(true);
    try {
      const t = await adminApi.updateToggles(patch);
      setToggles(t);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setWorking(false);
    }
  }

  if (!toggles) return null;

  return (
    <section className="brut bg-carp-paper p-5 space-y-4">
      <h2 className="font-display font-bold text-xl">Feature toggles</h2>
      {err && <div className="brut bg-carp-destructive text-white p-2 text-sm">{err}</div>}

      <ToggleRow
        title="Wystawianie faktur Subiekt 123"
        description={
          <>
            Master switch — wyłączone = ZERO faktur (ani auto, ani ręcznie via{" "}
            <code className="font-mono bg-carp-ink/10 px-1">/api/sales/&lt;id&gt;/invoice</code>).
            Włącz tylko gdy integracja Subiekt jest skonfigurowana i działa.
          </>
        }
        enabled={toggles.invoiceIssuingEnabled}
        working={working}
        onFlip={() => flip({ invoiceIssuingEnabled: !toggles.invoiceIssuingEnabled })}
      />

      <ToggleRow
        title="Karp mówi (broadcast głosowy)"
        description={
          <>
            Master kill switch dla broadcastu odpowiedzi. Wyłączone = ResponseJob trafia do
            kolejki ale worker oznacza go jako <strong>Skipped</strong> bez wołania voice
            backend. Awaryjnie ucisza Karpia bez redeployu.
          </>
        }
        enabled={toggles.voiceResponseEnabled}
        working={working}
        onFlip={() => flip({ voiceResponseEnabled: !toggles.voiceResponseEnabled })}
      />

      <ToggleRow
        title="Analiza widza (RAG + profil)"
        description={
          <>
            Wzbogaca prompt Karpia o profil widza (sub status, suma donate'ów, ranking, czy
            first-time) i similar messages z Qdrant. Wyłączone = Karp dostaje tylko bazowy
            prompt + recent chat buffer. Włącz dopiero gdy embed+Qdrant zielone na staging.
          </>
        }
        enabled={toggles.viewerAnalysisEnabled}
        working={working}
        onFlip={() => flip({ viewerAnalysisEnabled: !toggles.viewerAnalysisEnabled })}
      />

      <ToggleRow
        title="Karp odpowiada na chat"
        description={
          <>
            Karp reaguje na wiadomości czat z Kicka, trzymając kontekst{" "}
            <strong>ostatnich 30 wiadomości</strong>. Wymaga też <em>Karp mówi</em> = ON
            (master kill switch). Throttle: ~20s między odpowiedziami, wiadomości &lt;6 znaków i
            komendy <code className="font-mono bg-carp-ink/10 px-1">!</code>/
            <code className="font-mono bg-carp-ink/10 px-1">/</code> są pomijane.
          </>
        }
        enabled={toggles.chatReplyEnabled}
        working={working}
        onFlip={() => flip({ chatReplyEnabled: !toggles.chatReplyEnabled })}
      />
    </section>
  );
}

function ToggleRow(props: {
  title: string;
  description: ReactNode;
  enabled: boolean;
  working: boolean;
  onFlip: () => void;
}) {
  return (
    <div className="flex items-center justify-between gap-4 flex-wrap border-t-[2px] border-carp-ink/20 pt-3 first:border-t-0 first:pt-0">
      <div className="flex-1 min-w-[260px]">
        <p className="font-display font-bold text-sm uppercase">{props.title}</p>
        <p className="text-xs text-carp-ink/70 mt-1">{props.description}</p>
      </div>
      <button
        type="button"
        disabled={props.working}
        onClick={props.onFlip}
        className={`btn-primary ${props.enabled ? "!bg-carp-lime !text-carp-ink" : "!bg-carp-destructive"}`}
      >
        {props.enabled ? "✓ WŁĄCZONE" : "✗ WYŁĄCZONE"}
      </button>
    </div>
  );
}
